import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import "../../styles/Form.css";
import logo from "../../images/logo.png";

const DangerousWorkPolicy = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const [username, setUsername] = useState("");
  const userId = localStorage.getItem("userId");
  const [isSigned, setIsSigned] = useState(false);
  const [signedDate, setSignedDate] = useState("");
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const effectiveUserId = isAdmin && paramId ? paramId : userId;

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const token = localStorage.getItem("token");
        const url = isAdmin
          ? `${API_BASE}/api/users/${effectiveUserId}`
          : `${API_BASE}/api/user/profile`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { first_name, last_name } = res.data;
        setUsername(`${first_name} ${last_name}`);
      } catch (err) {
        console.error("Error fetching user name:", err);
      }
    };

    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${effectiveUserId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const TASK_ID = 13; // verify this matches nwt_tasks.t_id for LEEP
        const idKey = String(TASK_ID);
        const nameKey = "dangerous_work_policy";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        const signed = status === "review" || status === "completed";

        if (signed) {
          setIsSigned(true);
          setSignedDate(
            res.data.signdates?.[idKey] ?? res.data.signdates?.[nameKey],
          );
        }
      } catch (err) {
        console.error("Error checking task status:", err);
      }
    };

    fetchUserName();
    fetchTaskStatus();
  }, [API_BASE, userId, effectiveUserId, isAdmin]);

  const handleCancel = () => {
    if (isAdmin && paramId) {
      navigate(`/userhome/${paramId}`);
    } else {
      navigate("/userhome");
    }
  };

  return (
    <>
      {/* 🔝 Header Section with Status + Cancel Button */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Right to Refuse Dangerous Work Policy
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          Return
        </button>
      </div>

      <div
        className="form-wrapper my-4 print-area"
        style={{ fontSize: "15px", lineHeight: "1.6" }}
      >
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img
              src={logo}
              alt="Company Logo"
              style={{ width: "150px", height: "auto" }}
            />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Right to Refuse Dangerous Work Policy</h2>
          </div>
          <CompanyAddress />
        </div>

        <h5 className="mt-5">Intent</h5>
        <p>
          In accordance with the Canada Labour Code (Part II), in the event that
          a New-Way Trucking Limited employee encounters dangerous working
          conditions, or where the required equipment, tools or machinery
          present a serious health and safety concern, the employee shall have
          the right to refuse any work that they believe to be unsafe.
        </p>
        <p>
          The Right to Refuse Dangerous Work Policy applies to New-Way Trucking
          Limited, its employees, vendors, visitors and clients who are on the
          Company's premises or acting on behalf of the Company at all times and
          without exception.
        </p>

        <h5 className="mt-5">Guidelines</h5>
        <p>
          Under the CLC, employees have the right to refuse dangerous work if
          they have reason to believe that:
        </p>
        <ul>
          <li>
            The physical condition of the workplace or workstation is likely to
            endanger himself or herself;
          </li>
          <li>
            Any machine, equipment or tool that the employee is using or is told
            to use is likely to endanger himself or herself or another employee;
            or
          </li>
          <li>
            Performing an activity constitutes a danger to the employee or a
            co-worker
          </li>
        </ul>

        <h5 className="mt-5">Work Refusal Procedure</h5>
        <p>
          In the event of work being refused or stopped, the following actions
          are required of New-Way Trucking Limited and its employees:
        </p>
        <ol>
          <li>
            Employees must inform their supervisor/manager of the work refusal
            immediately, and provide an explanation detailing the rationale
            behind the refusal. Employees must stay nearby in a safe place until
            an investigation has been completed.
          </li>
          <li>
            If after investigation New-Way Trucking Limited agrees with the
            employee, immediate corrective action shall be implemented to
            protect employees. In this instance New-Way Trucking Limited shall
            inform the Health and Safety Committee of the matter and the action
            taken to resolve it. The work refusal would end here.
          </li>
          <li>
            In the event that after investigation New-Way Trucking Limited does
            not agree that a danger exist, or if the employee is dissatisfied
            with the corrective action, the employee may continue to refuse to
            work if they have reasonable grounds to base their continued refusal
            on. The matter must now be communicated to the organization as well
            as the Health and Safety Committee.
          </li>
          <li>
            Management shall conduct an investigation into the situation
            immediately after learning of the continued refusal and shall work
            to find an effective, safe and mutually agreeable resolution to the
            issue in the presence of the employee and either a Health and Safety
            Committee Member who represents the workers or another employee who
            has been chosen by the employee who is refusing to conduct the
            work.{" "}
          </li>
          <li>
            In the event that an employee is unsatisfied with the resolution and
            continues to refuse the work, they must notify New-Way Trucking
            Limited and the Health and Safety Committee of the continued
            refusal. New-Way Trucking Limited shall contact a Health and Safety
            Officer from the Labour Program and request that they provide
            assistance.{" "}
          </li>
          <li>
            While awaiting the arrival and findings of the Health and Safety
            Officer, New-Way Trucking Limited may assign other reasonable work
            during normal work hours for the employee that has refused work and
            may ask another qualified employee to conduct the work. Where a
            second employee is asked to perform work that has been refused, the
            second employee must be informed of the initial work refusal, and
            the reasons for the refusal. Note: The second employee has the same
            right to refuse the work as the first employee.
          </li>
          <li>
            After conducting an investigation the Health and Safety Officer will
            provide the findings of the investigation in writing to New-Way
            Trucking Limited and the employee. In the event that the work is
            determined to be safe, the employee shall be expected to return to
            work.
          </li>
        </ol>

        <h5 className="mt-5">Discipline for Refusal to Work</h5>
        <p>
          Employees are required to work in accordance with the Regulations set
          forth by the Canada Labour Code and have the right to seek their
          enforcement. New-Way Trucking Limited shall not penalize, dismiss,
          discipline, suspend or threaten to do any of these things to an
          employee who has obeyed the law, and the Regulations of the Code.
        </p>
        <p>
          In the event that a work refusal was made in bad faith, or if the
          employee continues to refuse the work after the Health and Safety
          Officer has found that the work is unlikely to endanger the employee,
          New-Way Trucking Limited may elect to utilize disciplinary action(s)
          up to and including termination of employment with cause.
        </p>

        <h5 className="mt-5">Acknowledgement and Agreement</h5>
        <p className="mt-4">
          I, <strong>{username}</strong>, acknowledge that I have read and
          understand the Right to Refuse Dangerous Work Policy of New-Way
          Trucking Limited. Further, I agree to adhere to this policy and will
          ensure that employees working under my direction adhere to this
          policy. I understand that if I violate the rules/procedures outlined
          in this policy, I may face disciplinary action, up to and including
          termination of employment.
        </p>

        <hr className="my-5" />

        <form>
          <div className="row mb-3 mt-5">
            <div className="col">
              <label className="form-label">Employee Name</label>
              <input
                type="text"
                className="form-control"
                value={username}
                readOnly
              />
            </div>
            <div className="col">
              <label className="form-label">Date</label>
              <input
                type="text"
                className="form-control"
                value={
                  isSigned && signedDate
                    ? new Date(signedDate).toLocaleDateString()
                    : new Date().toLocaleDateString()
                }
                readOnly
              />
            </div>
          </div>
        </form>

        {userId && (
          <>
            <SignatureManager
              taskName="dangerous_work_policy"
              taskId={13}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="dangerous_work_policy"
              taskId={13}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default DangerousWorkPolicy;
